import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { CampaignDayBudgetInfo } from '../../components/CampaignDayBudgetInfo/CampaignDayBudgetInfo';
import {
    ForecastValue,
    ForecastValueType
} from '../../components/ForecastValue/ForecastValue';
import { GridCampaignNameCell } from '../../components/GridCampaignNameCell/GridCampaignNameCell';
import { GridCampaignTypeCell } from '../../components/GridCampaignTypeCell/GridCampaignTypeCell';
import { MoneyCell } from '../../components/MoneyCell/MoneyCell';
import { MoneyText } from '../../components/MoneyText/MoneyText';
import { NumberCell } from '../../components/NumberCell/NumberCell';
import { NumberText } from '../../components/NumberText/NumberText';
import { RecommendationBudgetApplyCell } from '../../components/RecommendationBudgetApplyCell/RecommendationBudgetApplyCell';
import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { DayBudgetValue } from '../../typings/budget';
import { ColumnsSettings, ColumnsSettingsViewDict } from '../../typings/common';
import {
    CurrencyCode,
    GdRecommendationStatus
} from '../../typings/graphql-grid';
import {
    GridCellProps,
    GridColumnDecl,
    GridColumnsOptions,
    OptionalGridColumnDecl,
    SortDirection
} from '../../typings/grid';
import {
    DailyBudgetRecommendationsItem,
    RecommendationsItemsScreenActions,
    RecommendationType
} from '../../typings/recommendations';
import { StatKey } from '../stats/stats';

import * as keyset from './dailyBudgetRecommendations.i18n'; // import * as i18n from ' b:declarations_dailyBudgetRecommendations t:i18n';

const i18n = i18nFactory(keyset);

export function getStaticDailyBudgetRecommendationsColumns(
    actions: RecommendationsItemsScreenActions,
    login: string,
    currency: CurrencyCode
): Array<
    OptionalGridColumnDecl<
        DailyBudgetRecommendationsItem,
        {},
        GridColumnsOptions
    >
> {
    return [
        {
            title: i18n('type'),
            key: 'type',
            cellClass: 'campaign-type-cell',
            fixed: true,
            isSortable: true,
            defaultSortDirection: SortDirection.asc,
            minWidth: 105,
            CellComponent: React.memo(
                ({
                    rowData: { campaignType }
                }: GridCellProps<
                    DailyBudgetRecommendationsItem
                >): JSX.Element => <GridCampaignTypeCell type={campaignType} />
            )
        },
        {
            title: i18n('name'),
            key: 'name',
            fixed: true,
            isSortable: true,
            defaultSortDirection: SortDirection.asc,
            minWidth: 150,
            CellComponent: React.memo(
                ({
                    rowData: { name, id }
                }: GridCellProps<
                    DailyBudgetRecommendationsItem
                >): JSX.Element => <GridCampaignNameCell name={name} id={id} />
            )
        },
        {
            title: i18n('budget', {
                currency: mapStringToCurrencySymbol(currency)
            }),
            key: 'budget',
            isSortable: false,
            minWidth: 150,
            CellComponent: React.memo(
                ({
                    rowData: { budget, currentDailyBudget }
                }: GridCellProps<
                    DailyBudgetRecommendationsItem
                >): JSX.Element => (
                    <CampaignDayBudgetInfo
                        value={
                            {
                                ...budget.value,
                                sum: currentDailyBudget
                            } as DayBudgetValue
                        }
                        isCompactViewEnabled={false}
                    />
                )
            )
        },
        {
            title: i18n('recommended-budget', {
                currency: mapStringToCurrencySymbol(currency)
            }),
            key: 'recommended-budget',
            isSortable: false,
            minWidth: 260,
            CellComponent: React.memo(
                ({
                    rowData: {
                        name,
                        id,
                        budgetRecommendation,
                        status,
                        budgetRecommendationKeys,
                        isApplicable
                    }
                }: GridCellProps<
                    DailyBudgetRecommendationsItem
                >): JSX.Element => {
                    const { submitRecommendation } = actions;

                    return (
                        <RecommendationBudgetApplyCell
                            campaignName={name}
                            campaignId={id}
                            recommendationKeys={budgetRecommendationKeys}
                            currency={currency}
                            value={budgetRecommendation}
                            isApplicable={isApplicable}
                            onClick={submitRecommendation}
                            isInProgress={
                                status === GdRecommendationStatus.IN_PROGRESS
                            }
                        />
                    );
                }
            )
        },
        {
            title: i18n('clicks'),
            key: StatKey.STAT_CLICKS,
            align: 'right',
            isSortable: true,
            minWidth: 115,
            CellComponent: React.memo(
                ({
                    rowData: { clicks }
                }: GridCellProps<
                    DailyBudgetRecommendationsItem
                >): JSX.Element => <NumberCell num={clicks} />
            )
        },
        {
            title: i18n('clicks-forecast'),
            key: 'recommendations_clicks',
            align: 'right',
            isSortable: true,
            minWidth: 115,
            CellComponent: React.memo(
                ({
                    rowData: { clicksForecast }
                }: GridCellProps<
                    DailyBudgetRecommendationsItem
                >): JSX.Element => (
                    <ForecastValue type={ForecastValueType.positive}>
                        <NumberText
                            num={clicksForecast}
                            fractionalPartLength={0}
                        />
                    </ForecastValue>
                )
            )
        },
        {
            title: i18n('cost', {
                currency: mapStringToCurrencySymbol(currency)
            }),
            key: StatKey.STAT_COST,
            align: 'right',
            isSortable: true,
            minWidth: 120,
            CellComponent: React.memo(
                ({
                    rowData: { cost }
                }: GridCellProps<
                    DailyBudgetRecommendationsItem
                >): JSX.Element => (
                    <MoneyCell num={cost} fractionalPartLength={0} />
                )
            )
        },
        {
            title: i18n('cost-forecast', {
                currency: mapStringToCurrencySymbol(currency)
            }),
            key: 'recommendations_cost',
            align: 'right',
            isSortable: true,
            minWidth: 120,
            CellComponent: React.memo(
                ({
                    rowData: { costForecast }
                }: GridCellProps<
                    DailyBudgetRecommendationsItem
                >): JSX.Element => (
                    <ForecastValue type={ForecastValueType.neutral}>
                        <MoneyText
                            num={costForecast}
                            fractionalPartLength={0}
                        />
                    </ForecastValue>
                )
            )
        }
    ];
}

export function getDefaultDynamicDailyBudgetRecommendationsColumns(): Pick<
    ColumnsSettings,
    'columnViewInfo'
> {
    return {
        columnViewInfo: {
            type: {
                width: 105
            },
            name: {
                width: 250
            },
            budget: {
                width: 150
            },
            'recommended-budget': {
                width: 260
            },
            [StatKey.STAT_CLICKS]: {
                width: 115
            },
            recommendations_clicks: {
                width: 160
            },
            [StatKey.STAT_COST]: {
                width: 145
            },
            recommendations_cost: {
                width: 190
            },
            'cost-forecast': {
                width: 175
            }
        }
    };
}

export function getDailyBudgetRecommendationsColumns(
    columnsSettings: ColumnsSettingsViewDict,
    actions: RecommendationsItemsScreenActions,
    login: string,
    currency: CurrencyCode
): Array<GridColumnDecl<DailyBudgetRecommendationsItem>> {
    return getStaticDailyBudgetRecommendationsColumns(
        actions,
        login,
        currency
    ).map(column => {
        return {
            ...column,
            ...columnsSettings[RecommendationType.dailyBudget].columnViewInfo[
                column.key
            ]
        };
    });
}
